import React from 'react';
import { Plus, Calendar, Edit2, Trash2, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * Preview Symptoms Page
 * Read-only view with mock symptom data for demo purposes
 */

// Mock symptom data for preview
const MOCK_SYMPTOMS = [
  {
    id: '1',
    date: '2024-12-18',
    symptoms: ['Fatigue', 'Night Sweats'],
    notes: 'Felt more tired than usual after light activity. Night sweats were mild.'
  },
  {
    id: '2',
    date: '2024-12-12',
    symptoms: ['Fatigue', 'Swollen Lymph Nodes'],
    notes: 'Noticed slight swelling in neck area. Energy levels were low throughout the day.'
  },
  {
    id: '3',
    date: '2024-12-05',
    symptoms: ['Easy Bruising', 'Fatigue'],
    notes: 'Small bruise appeared on arm without known cause.'
  },
  {
    id: '4',
    date: '2024-11-28',
    symptoms: ['Frequent Infections', 'Fatigue'],
    notes: 'Mild cold symptoms lasting longer than expected.'
  }
];

const MOCK_MOOD_DATA = {
  today: null,
  canCheckIn: true
};

const MOOD_OPTIONS = [
  { score: 1, label: 'Very low', emoji: '😔' },
  { score: 2, label: 'Low', emoji: '😕' },
  { score: 3, label: 'Okay', emoji: '😐' },
  { score: 4, label: 'Good', emoji: '🙂' },
  { score: 5, label: 'Very good', emoji: '😊' },
];

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AU', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
};

const PreviewSymptomsPage = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2">
            Symptoms
          </h1>
          <p className="text-lg leading-relaxed text-slate-700">Track symptoms over time</p>
        </div>
        <Button
          disabled
          className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 text-sm sm:text-base opacity-50 cursor-not-allowed"
        >
          <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
          Add New
        </Button>
      </div>

      {/* Preview Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Eye className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Preview Mode</p>
          <p className="text-sm text-amber-700">This is sample data. Create a free account to track your own symptoms.</p>
        </div>
      </div>

      {/* Daily Check-in Section - Disabled */}
      <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
        <h3 className="text-base font-semibold text-slate-800 mb-3 text-center">
          How are you feeling today?
        </h3>
        
        <div className="flex justify-center gap-2 sm:gap-3 mb-2">
          {MOOD_OPTIONS.map((mood) => (
            <button
              key={mood.score}
              disabled
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center bg-white border border-slate-200 cursor-not-allowed opacity-60"
              title={`${mood.label} (Preview mode)`}
            >
              <span className="text-[24px] sm:text-[26px]">{mood.emoji}</span>
            </button>
          ))}
        </div>
        
        <p className="text-[10px] sm:text-xs text-slate-400 mt-2 text-center">
          Daily check-ins help you notice patterns over time.
        </p>
      </div>

      {/* Symptoms List - Read Only */}
      <div className="space-y-4">
        {MOCK_SYMPTOMS.map((symptomEntry) => (
          <div
            key={symptomEntry.id}
            className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <p className="text-lg font-semibold text-slate-900 mb-2">
                  {formatDate(symptomEntry.date)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {symptomEntry.symptoms.map((symptom, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {symptom}
                    </span>
                  ))}
                </div>
                {symptomEntry.notes && (
                  <div className="mt-3 bg-slate-50 rounded-lg p-3">
                    <p className="text-sm text-slate-700">{symptomEntry.notes}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {/* Disabled Edit Button */}
                <button
                  disabled
                  className="p-2 text-slate-300 cursor-not-allowed rounded-lg"
                  title="Edit (Preview mode)"
                >
                  <Edit2 className="h-5 w-5" />
                </button>
                {/* Disabled Delete Button */}
                <button
                  disabled
                  className="p-2 text-slate-300 cursor-not-allowed rounded-lg"
                  title="Delete (Preview mode)"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Common Symptoms Reference */}
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Common CLL/SLL Symptoms</h3>
        <p className="text-sm text-slate-600 mb-4">
          These are symptoms commonly associated with CLL/SLL. Tracking them can help you and your healthcare team monitor your condition.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {[
            'Fatigue', 'Night Sweats', 'Fever', 'Chills', 'Weight Loss', 'Swollen Lymph Nodes', 
            'Easy Bruising', 'Frequent Infections', 'Shortness of Breath', 'Abdominal Fullness',
            'Dizziness', 'Bone or Joint Pain', 'Nosebleeds', 'Bleeding Gums', 'Itchy Skin',
            'Pale Skin', 'Headaches', 'Muscle Weakness', 'Sore Throat', 'Tingling or numbness (hands/feet)',
            'Mouth Sores', 'Nausea', 'Skin Rash', 'Brain Fog', 'Sleep Problems',
            'Loss of Appetite', 'Anxiety', 'Depression'
          ].map((symptom) => (
            <div
              key={symptom}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 text-center"
            >
              {symptom}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PreviewSymptomsPage;
